import {colors, dimensions} from '@bettermarks/bm-ui-components';
import {css} from 'styled-components';
import {
  Size,
  Theme,
  ButtonProps,
  Variant,
  CommonProps,
  TabProps,
  RouterButtonProps
} from './types';

const colorMap: Record<Theme, string> = {
  [Theme.action]: colors.cBlue700,
  [Theme.neutral]: colors.cGray700
};

const hoverColorMap: Record<Theme, string> = {
  [Theme.action]: colors.cBlue800,
  [Theme.neutral]: colors.cGray800
};

const WHITE_SVG = `
  path,
  polygon {
    fill: ${colors.cWhite};
  }
`;

const THEMED_SVG = css<ButtonProps>`
  path,
  polygon {
    fill: ${p => colorMap[p.buttonTheme || Theme.action]};
  }
`;

const SOLID = css<ButtonProps>`
  background-color: ${p => colorMap[p.buttonTheme || Theme.action]};
  ${p => p.colorizeSVG && WHITE_SVG};
  &:hover {
    background-color: ${p => hoverColorMap[p.buttonTheme || Theme.action]};
  }
  color: ${colors.cWhite};
`;

const GHOST = css<ButtonProps>`
  background-color: transparent;
  color: ${p => colorMap[p.buttonTheme || Theme.action]};
  ${p => p.colorizeSVG && THEMED_SVG};
  &:hover {
    background-color: ${colors.cGray100};
  }
`;

const OUTLINE = css<ButtonProps>`
  background-color: transparent;
  color: ${p => colorMap[p.buttonTheme || Theme.action]};
  border-color: ${p => colorMap[p.buttonTheme || Theme.action]};
  ${p => p.colorizeSVG && THEMED_SVG};
  &:hover {
    background-color: ${p => hoverColorMap[p.buttonTheme || Theme.action]};
    color: ${colors.cWhite};
    border-color: ${p => hoverColorMap[p.buttonTheme || Theme.action]};
    ${p => p.colorizeSVG && WHITE_SVG};
  }
`;

const VARIANTS = css<ButtonProps>`
  ${p => {
    switch (p.variant) {
      case Variant.solid:
        return SOLID;
      case Variant.ghost:
        return GHOST;
      case Variant.outline:
        return OUTLINE;
      default:
        return SOLID;
    }
  }}
`;

const MEDIUM = css`
  font-size: ${dimensions.fontSizeM};
  line-height: ${dimensions.lineHeightM};
  padding: 9px ${dimensions.spaceS};
`;

const SIZES = css<CommonProps>`
  ${p => {
    switch (p.size) {
      case Size.xs:
        return css`
          font-size: ${dimensions.fontSizeXs};
          line-height: ${dimensions.lineHeightXs};
          padding: 7px ${dimensions.spaceS};
        `;
      case Size.sm:
        return css`
          font-size: ${dimensions.fontSizeS};
          line-height: 18px;
          padding: ${dimensions.spaceXs} ${dimensions.spaceS};
        `;
      case Size.md:
        return MEDIUM;
      case Size.lg:
        return css`
          font-size: ${dimensions.fontSizeM};
          line-height: ${dimensions.lineHeightM};
          padding: 11px ${dimensions.spaceS};
        `;
      default:
        return MEDIUM;
    }
  }}
`;

const DISABLED = css`
  background-color: ${colors.cGray300};
  color: ${colors.cWhite};
`;

const BASE = css<CommonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${dimensions.spaceXs};
  border-radius: ${dimensions.borderRadiusM};
  text-decoration: none;
  &,
  path,
  polygon {
    transition: all 0.25s ease-in-out;
  }
  border: 1px solid transparent;
`;

const TAB = css<TabProps>`
  cursor: pointer;
  ${p =>
    p.active
      ? css`
          color: ${colors.cBlue700};
          border-color: ${colors.cBlue700};
          background-color: ${colors.cBlue100};
          &:hover {
            background-color: ${colors.cBlue200};
          }
        `
      : css`
          color: ${colors.cGray700};
          border-color: ${colors.cGray700};
          background-color: transparent;
          &:hover {
            background-color: ${colors.cGray100};
          }
        `};
`;

export const BUTTON_STYLES = css<CommonProps & ButtonProps>`
  ${BASE};
  ${p => (p.disabled ? DISABLED : VARIANTS)};
  ${SIZES};
  cursor: ${p => (p.disabled ? 'default' : 'pointer')};
`;

const ROUTER_BASE = css<RouterButtonProps>`
  background-color: transparent;
  color: ${colors.cBlue700};

  &:hover {
    background-color: ${colors.cGray100};
  }
`;

export const ROUTER_STYLES = css<CommonProps & RouterButtonProps>`
  ${BASE};
  ${p => (p.disabled ? DISABLED : ROUTER_BASE)};
  ${SIZES};
`;

export const TAB_STYLES = css`
  ${BASE};
  ${SIZES};
  ${TAB};
`;
